import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { Link } from 'react-router-dom';
import api from '../../api';

interface AdminRoom {
  roomKey: string;
  examType: 'WAEC' | 'JAMB';
  subjectName: string;
  departments: string[];
  coordinatorIdIssued: boolean;
  accessEnabled: boolean;
}

interface AdminMeetingRequest {
  id: string;
  roomKey: string;
  examType: 'WAEC' | 'JAMB';
  subjectName: string;
  studentName: string;
  studentEmail: string;
  title: string;
  details: string;
  preferredStartAt: string | null;
  status: 'pending' | 'approved' | 'declined' | 'scheduled';
  adminNote: string;
  studentMessage: string;
  coordinatorMessage: string;
  createdAt: string;
}

interface AdminMeeting {
  id: string;
  roomKey: string;
  examType: 'WAEC' | 'JAMB';
  subjectName: string;
  title: string;
  status: 'scheduled' | 'live' | 'ended' | 'cancelled';
  scheduledStartAt: string;
  startedAt?: string;
  endedAt?: string;
  participantCount: number;
}

const AdminDiscussionRoomsTab: React.FC = () => {
  const [rooms, setRooms] = useState<AdminRoom[]>([]);
  const [requests, setRequests] = useState<AdminMeetingRequest[]>([]);
  const [meetings, setMeetings] = useState<AdminMeeting[]>([]);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [examType, setExamType] = useState<'WAEC' | 'JAMB'>('WAEC');
  const [loading, setLoading] = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [meetingsLoading, setMeetingsLoading] = useState(true);
  const [downloadingMeetingId, setDownloadingMeetingId] = useState('');
  const [deletingMeetingId, setDeletingMeetingId] = useState('');
  const [deletingAllHistory, setDeletingAllHistory] = useState(false);
  const [reviewingRequestId, setReviewingRequestId] = useState('');
  const [requestNotes, setRequestNotes] = useState<Record<string, string>>({});
  const [coordinatorRequestMessages, setCoordinatorRequestMessages] = useState<Record<string, string>>({});
  const [issuingRoomKey, setIssuingRoomKey] = useState('');
  const [generatedId, setGeneratedId] = useState<{ roomKey: string; subjectName: string; coordinatorId: string } | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requestAlert, setRequestAlert] = useState('');
  const previousPendingRequestIds = useRef<Set<string> | null>(null);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/discussion-rooms');
      setRooms(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to load discussion room controls.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRooms();
  }, [fetchRooms]);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await api.get('/admin/virtual-meeting-requests');
      const nextRequests: AdminMeetingRequest[] = Array.isArray(response.data?.data) ? response.data.data : [];
      const nextPendingCount = Number(response.data?.pendingCount) || 0;
      const nextPendingRequestIds = new Set(
        nextRequests.filter((request) => request.status === 'pending').map((request) => request.id)
      );
      setRequests(nextRequests);
      setPendingRequestCount(nextPendingCount);
      if (previousPendingRequestIds.current
        && [...nextPendingRequestIds].some((requestId) => !previousPendingRequestIds.current?.has(requestId))) {
        setRequestAlert('A new virtual session request has arrived.');
      }
      previousPendingRequestIds.current = nextPendingRequestIds;
      setError('');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to load virtual session requests.');
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  const fetchMeetings = useCallback(async () => {
    try {
      const response = await api.get('/admin/virtual-meetings');
      setMeetings(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to load session attendance.');
    } finally {
      setMeetingsLoading(false);
    }
  }, []);

  const downloadAttendance = async (meeting: AdminMeeting) => {
    setDownloadingMeetingId(meeting.id);
    try {
      const response = await api.get(`/admin/virtual-meetings/${meeting.id}/attendance`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `virtual-meeting-attendance-${meeting.id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to download session attendance.');
    } finally {
      setDownloadingMeetingId('');
    }
  };

  const deleteMeetingHistory = async (meeting: AdminMeeting) => {
    if (!window.confirm(`Delete "${meeting.title}" from session history? Students and coordinators will no longer see this session.`)) return;
    setDeletingMeetingId(meeting.id);
    setMeetings((current) => current.filter((item) => item.id !== meeting.id));
    try {
      await api.delete(`/admin/virtual-meetings/${meeting.id}`);
      setSuccess(`"${meeting.title}" was deleted from session history.`);
    } catch (requestError: any) {
      setMeetings((current) => [...current, meeting].sort((left, right) => (
        new Date(right.endedAt || right.startedAt || right.scheduledStartAt).getTime()
        - new Date(left.endedAt || left.startedAt || left.scheduledStartAt).getTime()
      )));
      setError(requestError?.response?.data?.message || 'Unable to delete session history.');
    } finally {
      setDeletingMeetingId('');
    }
  };

  const deleteAllMeetingHistory = async () => {
    const historyCount = meetings.filter((meeting) => ['ended', 'cancelled'].includes(meeting.status)).length;
    if (!historyCount || !window.confirm(`Delete all ended and cancelled meeting history? This cannot be undone. Active and scheduled sessions will be kept.`)) return;
    const previousMeetings = meetings;
    setDeletingAllHistory(true);
    setMeetings((current) => current.filter((meeting) => !['ended', 'cancelled'].includes(meeting.status)));
    try {
      const response = await api.delete('/admin/virtual-meetings/history');
      setSuccess(`${Number(response.data?.data?.deletedCount) || 0} ended or cancelled session records were deleted.`);
    } catch (requestError: any) {
      setMeetings(previousMeetings);
      setError(requestError?.response?.data?.message || 'Unable to delete session history.');
    } finally {
      setDeletingAllHistory(false);
    }
  };

  useEffect(() => {
    void fetchRequests();
    void fetchMeetings();
    const timer = window.setInterval(() => { void fetchRequests(); }, 30000);
    return () => window.clearInterval(timer);
  }, [fetchMeetings, fetchRequests]);

  const visibleRooms = useMemo(() => rooms.filter((room) => room.examType === examType), [rooms, examType]);

  const issueCoordinatorId = async (room: AdminRoom) => {
    if (room.coordinatorIdIssued && !window.confirm(`Generate a new ID for ${room.examType} ${room.subjectName}? The previous ID will stop working and existing coordinators will lose access.`)) {
      return;
    }
    setIssuingRoomKey(room.roomKey);
    setError('');
    setSuccess('');
    try {
      const response = await api.post(`/admin/discussion-rooms/${encodeURIComponent(room.roomKey)}/coordinator-id`);
      setGeneratedId({ roomKey: room.roomKey, subjectName: room.subjectName, coordinatorId: response.data?.data?.coordinatorId });
      setRooms((current) => current.map((item) => item.roomKey === room.roomKey ? { ...item, coordinatorIdIssued: true } : item));
      setSuccess(`A new coordinator ID was generated for ${room.examType} ${room.subjectName}.`);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to generate a coordinator ID.');
    } finally {
      setIssuingRoomKey('');
    }
  };

  const setRoomAccess = async (room: AdminRoom, enabled: boolean) => {
    setError('');
    setSuccess('');
    setRooms((current) => current.map((item) => item.roomKey === room.roomKey ? { ...item, accessEnabled: enabled } : item));
    try {
      await api.patch(`/admin/discussion-rooms/${encodeURIComponent(room.roomKey)}/access`, { enabled });
      setSuccess(`${room.examType} ${room.subjectName} is now ${enabled ? 'open' : 'closed'} for participants and coordinators.`);
    } catch (requestError: any) {
      setRooms((current) => current.map((item) => item.roomKey === room.roomKey ? { ...item, accessEnabled: room.accessEnabled } : item));
      setError(requestError?.response?.data?.message || 'Unable to update room access.');
    }
  };

  const copyCoordinatorId = async () => {
    if (!generatedId?.coordinatorId) return;
    try {
      await navigator.clipboard.writeText(generatedId.coordinatorId);
      setSuccess('Coordinator ID copied. Share it only with the room coordinator.');
    } catch {
      setError('Clipboard access was blocked. Select and copy the displayed ID manually.');
    }
  };

  const reviewRequest = async (request: AdminMeetingRequest, status: 'approved' | 'declined') => {
    setReviewingRequestId(request.id);
    setError('');
    setSuccess('');
    try {
      const response = await api.patch(`/admin/virtual-meeting-requests/${request.id}`, {
        status,
        adminNote: requestNotes[request.id] || '',
        studentMessage: requestNotes[request.id] || '',
        coordinatorMessage: coordinatorRequestMessages[request.id] || '',
      });
      setSuccess(`${request.examType} ${request.subjectName} request ${status}. ${status === 'approved' ? 'The room coordinator can now schedule it from the room’s virtual meetings tab.' : ''}`);
      setRequestNotes((current) => ({ ...current, [request.id]: '' }));
      setCoordinatorRequestMessages((current) => ({ ...current, [request.id]: '' }));
      await fetchRequests();
      if (response.data?.studentNotificationSent === false) {
        setError('The request decision was saved, but the student notification could not be created. The student will still see the admin message on their room page.');
      }
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to review this virtual session request.');
    } finally {
      setReviewingRequestId('');
    }
  };

  const pendingRequests = requests.filter((request) => request.status === 'pending');

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>Discussion room controls</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Issue room-specific coordinator IDs and review every subject room.
          </Typography>
        </Box>
        <Button component={Link} to="/discussion-rooms" state={{ adminRoomAccess: true }} variant="outlined" startIcon={<ForumOutlinedIcon />}>
          Open all rooms
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {requestAlert && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setRequestAlert('')}>{requestAlert}</Alert>}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="h6" component="h3">Live session attendance</Typography>
            <Typography variant="body2" color="text.secondary">Newest sessions appear first. Deleting history also removes its attendance/recording and reopens any linked request.</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              color="error"
              onClick={() => void deleteAllMeetingHistory()}
              disabled={deletingAllHistory || !meetings.some((meeting) => ['ended', 'cancelled'].includes(meeting.status))}
            >
              {deletingAllHistory ? 'Deleting history…' : 'Delete all history'}
            </Button>
            <Button size="small" onClick={() => void fetchMeetings()} disabled={meetingsLoading}>Refresh sessions</Button>
          </Stack>
        </Stack>
        {meetingsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={26} /></Box>
        ) : meetings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No virtual sessions are available yet.</Typography>
        ) : (
          <Stack spacing={1}>
            {[...meetings].sort((left, right) => (
              new Date(right.endedAt || right.startedAt || right.scheduledStartAt).getTime()
              - new Date(left.endedAt || left.startedAt || left.scheduledStartAt).getTime()
            )).map((meeting) => (
              <Paper key={meeting.id} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{meeting.title}</Typography>
                      <Chip size="small" label={`${meeting.examType} · ${meeting.subjectName}`} />
                      <Chip size="small" label={meeting.status} color={meeting.status === 'live' ? 'success' : 'default'} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {meeting.participantCount} recorded participant{meeting.participantCount === 1 ? '' : 's'} · {new Date(meeting.startedAt || meeting.scheduledStartAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => void downloadAttendance(meeting)}
                      disabled={Boolean(downloadingMeetingId) || deletingAllHistory}
                    >
                      {downloadingMeetingId === meeting.id ? 'Downloading…' : 'Download attendance'}
                    </Button>
                    {['ended', 'cancelled'].includes(meeting.status) && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => void deleteMeetingHistory(meeting)}
                        disabled={Boolean(deletingMeetingId) || deletingAllHistory}
                      >
                        {deletingMeetingId === meeting.id ? 'Deleting…' : 'Delete history'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" component="h3">Virtual session requests</Typography>
              <Chip size="small" color={pendingRequestCount ? 'warning' : 'default'} label={`${pendingRequestCount} pending`} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              This inbox refreshes every 30 seconds. Approving a request makes it visible to its room coordinator; only coordinators schedule meetings.
            </Typography>
          </Box>
          <Button size="small" onClick={() => void fetchRequests()} disabled={requestsLoading}>Refresh requests</Button>
        </Stack>
        {requestsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={26} /></Box>
        ) : pendingRequests.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No pending virtual session requests.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {pendingRequests.map((request) => (
              <Paper key={request.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{request.title}</Typography>
                      <Chip size="small" label={`${request.examType} · ${request.subjectName}`} />
                    </Stack>
                    <Typography variant="body2">{request.studentName} · {request.studentEmail}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{request.details || 'No additional details provided.'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {request.preferredStartAt ? `Preferred: ${new Date(request.preferredStartAt).toLocaleString()} · ` : ''}Requested {new Date(request.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Stack spacing={1} sx={{ minWidth: { md: 300 } }}>
                    <TextField
                      size="small"
                      label="Message for student"
                      value={requestNotes[request.id] || ''}
                      onChange={(event) => setRequestNotes((current) => ({ ...current, [request.id]: event.target.value }))}
                      inputProps={{ maxLength: 1000 }}
                      multiline
                      minRows={2}
                    />
                    <TextField
                      size="small"
                      label="Separate message for coordinator"
                      value={coordinatorRequestMessages[request.id] || ''}
                      onChange={(event) => setCoordinatorRequestMessages((current) => ({ ...current, [request.id]: event.target.value }))}
                      inputProps={{ maxLength: 1000 }}
                      multiline
                      minRows={2}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={Boolean(reviewingRequestId) || !requestNotes[request.id]?.trim() || !coordinatorRequestMessages[request.id]?.trim()}
                        onClick={() => void reviewRequest(request, 'approved')}
                      >
                        {reviewingRequestId === request.id ? 'Saving…' : 'Approve for coordinator'}
                      </Button>
                      <Button
                        color="error"
                        size="small"
                        disabled={Boolean(reviewingRequestId) || !requestNotes[request.id]?.trim() || !coordinatorRequestMessages[request.id]?.trim()}
                        onClick={() => void reviewRequest(request, 'declined')}
                      >
                        Decline
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
      {generatedId && (
        <Alert
          severity="warning"
          sx={{ mb: 2, alignItems: 'center' }}
          action={<Button color="inherit" size="small" startIcon={<ContentCopyOutlinedIcon />} onClick={() => void copyCoordinatorId()}>Copy ID</Button>}
        >
          <Typography variant="subtitle2">Coordinator ID for {generatedId.subjectName}</Typography>
          <Typography component="code" sx={{ display: 'block', fontSize: '1rem', fontWeight: 700, letterSpacing: 1.2, mt: 0.5, overflowWrap: 'anywhere' }}>
            {generatedId.coordinatorId}
          </Typography>
          <Typography variant="caption">This ID is displayed only now. Save it and share it with the assigned coordinator.</Typography>
        </Alert>
      )}

      <Tabs value={examType} onChange={(_, value) => setExamType(value)} aria-label="Manage exam discussion rooms" sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab value="WAEC" label="WAEC rooms" />
        <Tab value="JAMB" label="JAMB rooms" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : visibleRooms.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">No {examType} subjects are configured yet.</Typography>
        </Paper>
      ) : (
        <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
          {visibleRooms.map((room) => (
            <Box key={room.roomKey} sx={{ py: 2, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{room.subjectName}</Typography>
                  <Chip size="small" label={room.examType} variant="outlined" />
                  <Chip size="small" color={room.coordinatorIdIssued ? 'success' : 'default'} label={room.coordinatorIdIssued ? 'ID issued' : 'No coordinator ID'} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {room.departments.length === 3 ? 'All departments' : room.departments.join(', ')}
                </Typography>
              </Box>
              <Button
                variant={room.coordinatorIdIssued ? 'outlined' : 'contained'}
                onClick={() => void issueCoordinatorId(room)}
                disabled={Boolean(issuingRoomKey)}
                sx={{ flexShrink: 0 }}
              >
                {issuingRoomKey === room.roomKey ? 'Generating…' : room.coordinatorIdIssued ? 'Regenerate ID' : 'Generate coordinator ID'}
              </Button>
              <FormControlLabel
                control={<Switch checked={room.accessEnabled} onChange={(_, checked) => void setRoomAccess(room, checked)} />}
                label={room.accessEnabled ? 'Room open' : 'Room closed'}
                sx={{ ml: { sm: 1 }, mr: 0 }}
              />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default AdminDiscussionRoomsTab;
